import type { InputOf, OutputOf } from './generated/contracts';

/**
 * **The seller CSV import's screen rules** (D55), shared by the phone's screens and the
 * web's team harness so the two cannot disagree about what a column becomes or what the
 * seller still has to decide. Pure: no transport, no React, nothing platform-specific.
 *
 * The server is the authority — `catalog.import.confirm` re-checks everything here and
 * answers `MAPPING_INVALID` with its reasons. These rules exist so the screen can say what
 * is missing before the seller taps Confirm, not instead of the server saying it.
 */

export type ImportJob = OutputOf<'catalog.import.status'>['job'];
export type ImportProposal = NonNullable<ImportJob['proposal']>;
export type ProposedColumn = ImportProposal['columns'][number];
export type ImportTarget = ProposedColumn['suggested'];
export type ImportMapping = InputOf<'catalog.import.confirm'>['mapping'];
export type CategoryResolution = NonNullable<InputOf<'catalog.import.confirm'>['categories']>;
export type ImportRow = NonNullable<OutputOf<'catalog.import.status'>['rows']>['items'][number];

/** The placeholder category an import may leave a draft in; publish refuses it (`CATEGORY_UNASSIGNED`). */
export const UNCATEGORIZED = 'uncategorized';

/** Below this a suggestion is shown for review. Mirrors the server's `REVIEW_CONFIDENCE` (a spec holds them equal). */
export const REVIEW_CONFIDENCE = 80;

/** The import's own fields, in the order a picker lists them. `skip` is last on purpose. */
const FIELD_LABELS: Record<string, string> = {
  title: 'Title',
  category: 'Category',
  description: 'Description',
  condition: 'Condition',
  brand: 'Brand',
  condition_notes: 'Condition notes',
  sku: 'SKU',
  sale_type: 'Sale type',
  price: 'Price',
  starting_bid: 'Starting bid',
  reserve_price: 'Reserve price',
  buy_now_price: 'Buy now price',
  quantity: 'Quantity',
  images: 'Photos',
  weight: 'Weight',
  skip: 'Don’t import',
};

/** Fields several columns may feed — photos join in order, category parts into a path. */
const MULTI_COLUMN_TARGETS = new Set(['images', 'category', 'skip']);

/** An attribute from the dictionary, as a picker shows it. */
export interface AttributeLabel {
  key: string;
  label: string;
}

const ATTRIBUTE_PREFIX = 'attribute:';

/** What a target is called on screen: a field's name, or the attribute's label from the dictionary. */
export function targetLabel(target: ImportTarget, attributes: readonly AttributeLabel[] = []): string {
  if (target.startsWith(ATTRIBUTE_PREFIX)) {
    const key = target.slice(ATTRIBUTE_PREFIX.length);
    return attributes.find((attribute) => attribute.key === key)?.label ?? key;
  }
  return FIELD_LABELS[target] ?? target;
}

/** Everything a column can become: the fields, then the attributes by label, then "Don't import". */
export function targetOptions(attributes: readonly AttributeLabel[] = []): { value: ImportTarget; label: string }[] {
  const fields = Object.keys(FIELD_LABELS).filter((target) => target !== 'skip');
  return [
    ...fields.map((target) => ({ value: target, label: FIELD_LABELS[target] ?? target })),
    ...[...attributes]
      .sort((a, b) => a.label.localeCompare(b.label))
      .map((attribute) => ({ value: `${ATTRIBUTE_PREFIX}${attribute.key}`, label: attribute.label })),
    { value: 'skip', label: FIELD_LABELS.skip ?? 'skip' },
  ];
}

/** The seller's choice per column, by index — what the matching screen edits. */
export type ColumnChoices = Record<number, ImportTarget>;

/** The proposal as suggested: every column at its suggestion, which is what "accept all" sends. */
export function initialChoices(proposal: ImportProposal): ColumnChoices {
  return Object.fromEntries(proposal.columns.map((column) => [column.index, column.suggested]));
}

export function toMapping(proposal: ImportProposal, choices: ColumnChoices): ImportMapping {
  return proposal.columns.map((column) => ({ index: column.index, target: choices[column.index] ?? 'skip' }));
}

/** Columns the seller should look at: below the confidence bar, and still at the suggestion. */
export function columnsToReview(proposal: ImportProposal, choices: ColumnChoices): ProposedColumn[] {
  return proposal.columns.filter((column) => column.needsReview && choices[column.index] === column.suggested);
}

export function categoryColumns(proposal: ImportProposal, choices: ColumnChoices): ProposedColumn[] {
  return proposal.columns.filter((column) => choices[column.index] === 'category');
}

/**
 * **Which category question the screen asks** (D55):
 *
 * - `whole-import` — no column is category, so one category for every row is required
 *   ("Uncategorized" at the bottom);
 * - `values` — one category column with its values listed: each value with our best match,
 *   and the ones below the bar flagged;
 * - `none` — the column's values were not listed (more than 100, or several columns joined
 *   into a path): what does not match falls back to Uncategorized, with nothing to ask.
 */
export type CategoryQuestion =
  | { kind: 'whole-import' }
  | {
      kind: 'values';
      values: { value: string; count: number; match?: { categoryId: string; confidence: number }; flagged: boolean }[];
    }
  | { kind: 'none' };

export function categoryQuestion(proposal: ImportProposal, choices: ColumnChoices): CategoryQuestion {
  const columns = categoryColumns(proposal, choices);
  if (columns.length === 0) return { kind: 'whole-import' };
  const [only] = columns;
  if (columns.length > 1 || !only?.distinct) return { kind: 'none' };
  return {
    kind: 'values',
    values: only.distinct.map((entry) => ({
      value: entry.value,
      count: entry.count,
      ...(entry.category ? { match: entry.category } : {}),
      flagged: !entry.category || entry.category.confidence < REVIEW_CONFIDENCE,
    })),
  };
}

/**
 * The value matches to send: every value the screen listed, at the seller's choice or our
 * confident match. A flagged value the seller left alone is omitted, so it falls back —
 * the server will not trust a guess the seller never saw.
 */
export function categoryValues(
  question: CategoryQuestion,
  chosen: Record<string, string>,
): NonNullable<CategoryResolution['values']> {
  if (question.kind !== 'values') return [];
  return question.values.flatMap((entry) => {
    const category = chosen[entry.value] ?? (entry.flagged ? undefined : entry.match?.categoryId);
    return category ? [{ value: entry.value, category }] : [];
  });
}

/** What stops Confirm, in the seller's words — the server's rules, said before the tap. */
export function mappingProblems(
  proposal: ImportProposal,
  choices: ColumnChoices,
  options: { attributes?: readonly AttributeLabel[]; fallback?: string } = {},
): string[] {
  const problems: string[] = [];
  const targets = proposal.columns.map((column) => choices[column.index] ?? 'skip');
  if (!targets.includes('title')) problems.push('Choose the column that holds each product’s title.');
  const seen = new Set<string>();
  for (const target of targets) {
    if (MULTI_COLUMN_TARGETS.has(target)) continue;
    if (seen.has(target)) {
      problems.push(`Only one column can be ${targetLabel(target, options.attributes)}.`);
    }
    seen.add(target);
  }
  if (!targets.includes('category') && options.fallback === undefined) {
    problems.push('Choose a category for this import, or Uncategorized.');
  }
  return [...new Set(problems)];
}

/**
 * **Where a job is, in a seller's words**, and whether the screen should keep polling.
 * `progress` is 0–1 when there is a number to show.
 */
export interface ImportStage {
  title: string;
  detail?: string;
  progress?: number;
  /** Still moving: the screen polls `catalog.import.status` while this is true. */
  running: boolean;
}

export function importStage(job: ImportJob): ImportStage {
  const { counts } = job;
  if (job.stalled) {
    return {
      title: 'This import has stopped',
      detail: 'Nothing has moved for a while. We have been alerted; you do not need to upload it again.',
      running: false,
    };
  }
  switch (job.status) {
    case 'awaiting_upload':
      return { title: 'Waiting for the file', running: false };
    case 'queued':
    case 'verifying':
      return { title: 'Checking your file', detail: 'This takes a few seconds.', running: true };
    case 'rejected':
      return {
        title: 'This file can’t be imported',
        detail: 'Nothing was imported. Fix the file and try again.',
        running: false,
      };
    case 'awaiting_confirmation':
      return { title: 'Match your columns', running: false };
    case 'expired':
      return { title: 'This import expired', detail: 'It waited more than a day. Start a new one.', running: false };
    case 'importing':
      return {
        title: 'Importing your products',
        detail: `${counts.processed} of ${counts.total}`,
        progress: counts.total > 0 ? counts.processed / counts.total : 0,
        running: true,
      };
    case 'fetching_images':
      return {
        title: 'Adding photos',
        detail: `${counts.imagesFetched} added${counts.imagesFailed > 0 ? `, ${counts.imagesFailed} failed` : ''}`,
        running: true,
      };
    case 'imported':
      return { title: 'Imported as drafts', detail: importedSummary(job), running: false };
    case 'publishing': {
      const done = counts.published + counts.publishFailed + counts.publishSkipped;
      const drafts = counts.created + counts.updated + counts.unchanged;
      return {
        title: 'Publishing',
        detail: `${counts.published} published`,
        ...(drafts > 0 ? { progress: Math.min(1, done / drafts) } : {}),
        running: true,
      };
    }
    case 'published':
      return {
        title: counts.published > 0 ? 'Published' : 'Nothing published yet',
        detail: `${counts.published} on sale${counts.publishFailed > 0 ? ` · ${counts.publishFailed} still drafts` : ''}`,
        running: false,
      };
  }
}

function importedSummary(job: ImportJob): string {
  const { counts } = job;
  const parts = [`${counts.created} new`];
  if (counts.updated > 0) parts.push(`${counts.updated} updated`);
  if (counts.failed > 0) parts.push(`${counts.failed} failed`);
  if (counts.uncategorized > 0) parts.push(`${counts.uncategorized} need a category`);
  return parts.join(' · ');
}

/** Whether the seller may publish this import now (from `imported`, or again after a publish). */
export function canPublish(job: ImportJob): boolean {
  return (job.status === 'imported' || job.status === 'published') && !job.stalled;
}

/** A row's problems, worst first, as lines — the import's and the publish's together. */
export function rowProblems(row: ImportRow): { severity: 'error' | 'warning'; message: string }[] {
  const all = [
    ...row.problems,
    ...(row.publish?.problems ?? []),
    ...row.images
      .filter((image) => image.outcome === 'failed')
      .map((image) => ({
        severity: 'warning' as const,
        message: `A photo could not be added (${image.code ?? 'failed'}).`,
      })),
  ];
  return all
    .map((problem) => ({ severity: problem.severity, message: problem.message }))
    .sort((a, b) => (a.severity === b.severity ? 0 : a.severity === 'error' ? -1 : 1));
}
